import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, DollarSignIcon, TrendingUpIcon, DownloadIcon, FileSpreadsheetIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Opportunity, OpportunityStage } from '../../types';
import { formatCurrency, getStageName, getStageColor } from '../../utils/helpers';
import { exportOpportunitiesToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
export function Opportunities() {
  const {
    opportunities,
    addOpportunity,
    updateOpportunity,
    deleteOpportunity,
    contacts,
    companies
  } = useData();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactId: '',
    companyId: '',
    company: '',
    industry: '',
    size: '',
    location: '',
    stage: 'contacto_inicial' as OpportunityStage,
    amount: 0,
    probability: 0,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredOpportunities = opportunities.filter(o => {
    const companyName = o.companyId ? companies.find(c => c.id === o.companyId)?.name || o.company : o.company;
    const matchesSearch = o.name.toLowerCase().includes(searchTerm.toLowerCase()) || companyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = !filterStage || o.stage === filterStage;
    return matchesSearch && matchesStage;
  });
  const {
    paginatedItems: paginatedOpportunities,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredOpportunities);
  const stageOptions = [{
    value: 'contacto_inicial',
    label: 'Contacto Inicial'
  }, {
    value: 'evaluacion',
    label: 'Evaluación'
  }, {
    value: 'propuesta',
    label: 'Propuesta / Demo'
  }, {
    value: 'negociacion',
    label: 'Negociación'
  }, {
    value: 'completado',
    label: 'Cerrado / Completado'
  }];
  const contactOptions = contacts.map(c => ({
    value: c.id,
    label: c.name
  }));
  const companyOptions = companies.map(c => ({
    value: c.id,
    label: c.name
  }));
  const openNewForm = () => {
    setSelectedOpportunity(null);
    setFormData({
      name: '',
      contactId: '',
      companyId: '',
      company: '',
      industry: '',
      size: '',
      location: '',
      stage: 'contacto_inicial',
      amount: 0,
      probability: 0,
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setFormData({
      name: opportunity.name,
      contactId: opportunity.contactId || '',
      companyId: opportunity.companyId || '',
      company: opportunity.company,
      industry: opportunity.industry,
      size: opportunity.size,
      location: opportunity.location,
      stage: opportunity.stage,
      amount: opportunity.amount,
      probability: opportunity.probability,
      notes: opportunity.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsDeleteConfirmOpen(true);
  };
  const handleCompanyChange = (companyId: string) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    if (selectedCompany) {
      setFormData({
        ...formData,
        companyId: companyId,
        company: selectedCompany.name,
        industry: selectedCompany.industry,
        size: selectedCompany.size,
        location: selectedCompany.location
      });
    } else {
      setFormData({
        ...formData,
        companyId: companyId
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    // Require companyId if companies exist, otherwise allow legacy string
    if (companies.length > 0 && !formData.companyId) {
      errors.company = 'La empresa es requerida';
    } else if (!formData.company && !formData.companyId) {
      errors.company = 'La empresa es requerida';
    }
    if (formData.amount < 0) errors.amount = 'El monto no puede ser negativo';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const opportunityData = {
      ...formData,
      contactId: formData.contactId || null,
      companyId: formData.companyId || null
    };
    if (selectedOpportunity) {
      updateOpportunity(selectedOpportunity.id, opportunityData);
    } else {
      addOpportunity(opportunityData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (selectedOpportunity) {
      deleteOpportunity(selectedOpportunity.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportOpportunitiesToExcel(opportunities);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-1346">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1347">
        <div data-id="element-1348">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1349">
            Oportunidades
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1350">
            Gestión de oportunidades comerciales B2B
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1352" />} onClick={openNewForm} data-id="element-1351">
          Nueva Oportunidad
        </Button>
      </div>

      {/* Beautiful Export Card - Only for Admin */}
      {isAdmin && <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200 dark:border-emerald-800" data-id="element-1353">
          <div className="flex items-center justify-between" data-id="element-1354">
            <div className="flex items-center gap-4" data-id="element-1355">
              <div className="w-14 h-14 rounded-xl bg-emerald-500 dark:bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20" data-id="element-1356">
                <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-1357" />
              </div>
              <div data-id="element-1358">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1359">
                  Exportar Base de Datos
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1360">
                  Descarga todas las oportunidades en formato Excel
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-1361">
                  <div className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400" data-id="element-1362">
                    <TrendingUpIcon className="w-4 h-4" data-id="element-1363" />
                    <span className="font-medium" data-id="element-1364">
                      {opportunities.length} oportunidades
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1365">
                    • Incluye: Nombre, Empresa, Etapa, Monto, Probabilidad
                  </div>
                </div>
              </div>
            </div>
            <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1367" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-200" data-id="element-1366">
              {isExporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </Card>}

      {/* Filters */}
      <Card data-id="element-1368">
        <div className="flex items-center gap-4" data-id="element-1369">
          <div className="flex-1 relative" data-id="element-1370">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1371" />
            <Input placeholder="Buscar oportunidades..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-1372" />
          </div>
          <div className="w-64" data-id="element-1373">
            <Select options={[{
            value: '',
            label: 'Todas las etapas'
          }, ...stageOptions]} value={filterStage} onChange={setFilterStage} placeholder="Filtrar por etapa" data-id="element-1374" />
          </div>
        </div>
      </Card>

      {/* Opportunities List */}
      <Card padding="none" data-id="element-1375">
        <div className="overflow-x-auto" data-id="element-1376">
          <table className="w-full" data-id="element-1377">
            <thead data-id="element-1378">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1379">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1380">
                  Oportunidad
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1381">
                  Empresa
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1382">
                  Etapa
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1383">
                  Monto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1384">
                  Probabilidad
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1385">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1386">
              {filteredOpportunities.length === 0 ? <tr data-id="element-1387">
                  <td colSpan={6} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1388">
                    No se encontraron oportunidades
                  </td>
                </tr> : paginatedOpportunities.map(opp => {
              const companyName = opp.companyId ? companies.find(c => c.id === opp.companyId)?.name || opp.company : opp.company;
              return <tr key={opp.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-1389">
                      <td className="py-4 px-6" data-id="element-1390">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-1391">
                          {opp.name}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-1392">
                        <p className="text-gray-900 dark:text-gray-100" data-id="element-1393">
                          {companyName}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1394">
                          {opp.industry}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-1395">
                        <div className="flex items-center gap-2" data-id="element-1396">
                          <div className={`w-2 h-2 rounded-full ${getStageColor(opp.stage)}`} data-id="element-1397" />
                          <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1398">
                            {getStageName(opp.stage)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900 dark:text-gray-100" data-id="element-1399">
                        {formatCurrency(opp.amount)}
                      </td>
                      <td className="py-4 px-6" data-id="element-1400">
                        <div className="flex items-center gap-2" data-id="element-1401">
                          <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-1402">
                            <div className="h-full bg-sky-500" style={{
                        width: `${opp.probability}%`
                      }} data-id="element-1403" />
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1404">
                            {opp.probability}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-1405">
                        <div className="flex items-center justify-end gap-2" data-id="element-1406">
                          <button onClick={() => openEditForm(opp)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" data-id="element-1407">
                            <EditIcon className="w-4 h-4" data-id="element-1408" />
                          </button>
                          <button onClick={() => openDeleteConfirm(opp)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-1409">
                            <TrashIcon className="w-4 h-4" data-id="element-1410" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4" data-id="element-1411">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-1412" />
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedOpportunity ? 'Editar Oportunidad' : 'Nueva Oportunidad'} size="lg" icon={<TrendingUpIcon className="w-5 h-5" data-id="element-1414" />} accentColor="emerald" data-id="element-1413">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1415">
          {/* Basic Info */}
          <div data-id="element-1416">
            <div className="flex items-center gap-2 mb-4" data-id="element-1417">
              <span className="w-1.5 h-5 rounded-full bg-emerald-500" data-id="element-1418"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1419">
                Información General
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1420">
              <Input label="Nombre de la Oportunidad" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Contrato de servicios médicos" data-id="element-1421" />
              <div className="grid grid-cols-2 gap-4" data-id="element-1422">
                <Select label="Empresa" options={companyOptions} value={formData.companyId} onChange={handleCompanyChange} searchable error={formErrors.company} data-id="element-1423" />
                <Select label="Contacto Principal" options={contactOptions} value={formData.contactId} onChange={value => setFormData({
                ...formData,
                contactId: value
              })} searchable data-id="element-1424" />
              </div>
            </div>
          </div>

          {/* Company Details */}
          <div data-id="element-1425">
            <div className="flex items-center gap-2 mb-4" data-id="element-1426">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-1427"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1428">
                Detalles de la Empresa
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1429">
              <div className="grid grid-cols-2 gap-4" data-id="element-1430">
                <Input label="Industria" value={formData.industry} onChange={e => setFormData({
                ...formData,
                industry: e.target.value
              })} placeholder="Ej: Salud, Tecnología" data-id="element-1431" />
                <Input label="Tamaño de Empresa" value={formData.size} onChange={e => setFormData({
                ...formData,
                size: e.target.value
              })} placeholder="Ej: 50-100 empleados" data-id="element-1432" />
              </div>
              <Input label="Ubicación" value={formData.location} onChange={e => setFormData({
              ...formData,
              location: e.target.value
            })} placeholder="Ej: Santo Domingo, RD" data-id="element-1433" />
            </div>
          </div>

          {/* Deal Info */}
          <div data-id="element-1434">
            <div className="flex items-center gap-2 mb-4" data-id="element-1435">
              <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-1436"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1437">
                Datos del Negocio
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1438">
              <div className="grid grid-cols-2 gap-4" data-id="element-1439">
                <Select label="Etapa" options={stageOptions} value={formData.stage} onChange={value => setFormData({
                ...formData,
                stage: value as OpportunityStage
              })} data-id="element-1440" />
                <Input type="number" label="Monto Estimado (DOP)" value={formData.amount} onChange={e => setFormData({
                ...formData,
                amount: parseFloat(e.target.value) || 0
              })} error={formErrors.amount} min={0} data-id="element-1441" />
              </div>
              <div data-id="element-1442">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1443">
                  Probabilidad de Cierre
                </label>
                <div className="flex items-center gap-4" data-id="element-1444">
                  <input type="range" min="0" max="100" step="5" value={formData.probability} onChange={e => setFormData({
                  ...formData,
                  probability: parseInt(e.target.value)
                })} className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-emerald-500" data-id="element-1445" />
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1 rounded-lg min-w-[60px] text-center" data-id="element-1446">
                    {formData.probability}%
                  </span>
                </div>
              </div>
              <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} rows={3} placeholder="Notas adicionales sobre la oportunidad..." data-id="element-1447" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-1448">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1449">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1450">
              {selectedOpportunity ? 'Guardar Cambios' : 'Crear Oportunidad'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-1451">
        <div className="space-y-4" data-id="element-1452">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1453">
            ¿Está seguro de que desea eliminar la oportunidad "
            {selectedOpportunity?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-1454">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1455">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1456">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}